// Wave 2 — Record a new material price observation.
// Source-type selector drives the required fields shown. Manual/vendor/research
// support optional file upload as truthful provenance.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";

const SOURCE_TYPES = [
  { key: "vendor_portal", label: "Vendor Portal Quote", requires_url: false, allows_upload: true },
  { key: "manual_upload", label: "Manual Upload (Receipt / Screenshot)", requires_url: false, allows_upload: true },
  { key: "marketplace_research", label: "Marketplace Research", requires_url: true, allows_upload: true },
  { key: "home_depot_pro", label: "Home Depot Pro (Scaffold — records as UNVERIFIED)", requires_url: false, allows_upload: false },
  { key: "lowes_pro", label: "Lowe's Pro (Scaffold — records as UNVERIFIED)", requires_url: false, allows_upload: false },
  { key: "licensed_provider", label: "Licensed Provider slot (Scaffold — records as UNVERIFIED)", requires_url: false, allows_upload: false },
  { key: "direct_supplier", label: "Direct Supplier slot (Scaffold — records as UNVERIFIED)", requires_url: false, allows_upload: false },
];

export default function MaterialPriceNew() {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState("vendor_portal");
  const [f, setF] = useState({
    source_provider_key: "",
    source_url: "",
    product_description: "",
    manufacturer: "",
    model_sku: "",
    unit_of_measure: "each",
    quantity_required: "",
    price_per_unit: "",
    store_or_supplier: "",
    supplier_location: "",
    observed_at: new Date().toISOString().slice(0, 16),
    freight: "",
    taxes: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const meta = SOURCE_TYPES.find((s) => s.key === sourceType);

  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!f.product_description || !f.price_per_unit || !f.store_or_supplier || !f.source_provider_key) {
      toast.error("Product, price, supplier, and source provider key are required.");
      return;
    }
    setSubmitting(true);
    const observed_at = new Date(f.observed_at).toISOString();
    try {
      if (file && meta?.allows_upload) {
        const form = new FormData();
        form.append("file", file);
        form.append("source_type", sourceType);
        form.append("source_provider_key", f.source_provider_key);
        form.append("product_description", f.product_description);
        form.append("unit_of_measure", f.unit_of_measure);
        form.append("price_per_unit", String(Number(f.price_per_unit || 0)));
        form.append("store_or_supplier", f.store_or_supplier);
        form.append("observed_at", observed_at);
        if (f.manufacturer) form.append("manufacturer", f.manufacturer);
        if (f.model_sku) form.append("model_sku", f.model_sku);
        if (f.quantity_required) form.append("quantity_required", String(Number(f.quantity_required)));
        if (f.supplier_location) form.append("supplier_location", f.supplier_location);
        if (f.freight) form.append("freight", String(Number(f.freight)));
        if (f.taxes) form.append("taxes", String(Number(f.taxes)));
        if (f.source_url) form.append("source_url", f.source_url);
        if (f.notes) form.append("notes", f.notes);
        const r = await api.post("/material-prices/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Price recorded with receipt attached");
        navigate(`/procurement/prices/${r.data.id}`);
      } else {
        const body = {
          source_type: sourceType,
          source_provider_key: f.source_provider_key,
          source_url: f.source_url || null,
          product_description: f.product_description,
          manufacturer: f.manufacturer || null,
          model_sku: f.model_sku || null,
          unit_of_measure: f.unit_of_measure,
          quantity_required: f.quantity_required ? Number(f.quantity_required) : null,
          price_per_unit: Number(f.price_per_unit),
          currency: "USD",
          store_or_supplier: f.store_or_supplier,
          supplier_location: f.supplier_location || null,
          freight: f.freight ? Number(f.freight) : null,
          taxes: f.taxes ? Number(f.taxes) : null,
          observed_at,
          notes: f.notes || null,
        };
        const r = await api.post("/material-prices", body);
        toast.success("Price recorded");
        navigate(`/procurement/prices/${r.data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell
      title="Record material price"
      subtitle="Every observation is stamped with source, timestamp, verification status, and audit ID. Scaffold sources auto-mark as UNVERIFIED."
    >
      <form onSubmit={submit} className="max-w-3xl">
        <Card className="border-erf-borderCream mb-4">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label>Source type</Label>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className="mt-1 w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white"
                data-testid="mp-source-type"
              >
                {SOURCE_TYPES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              {meta?.requires_url && (
                <div className="mt-2 text-[11px] font-mono text-erf-goldHover">
                  Marketplace research requires the source URL below.
                </div>
              )}
              {!meta?.allows_upload && (
                <div className="mt-2 text-[11px] font-mono text-erf-dark/60">
                  Scaffold source — no live API confirmation yet. Records save as UNVERIFIED.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source provider key <span className="text-erf-goldHover">*</span></Label>
                <Input
                  value={f.source_provider_key}
                  onChange={(e) => upd("source_provider_key", e.target.value)}
                  placeholder='e.g. "vendor:acme_lumber" or "manual:home_depot_receipt"'
                  data-testid="mp-source-provider-key"
                />
              </div>
              <div>
                <Label>Source URL {meta?.requires_url && <span className="text-erf-goldHover">*</span>}</Label>
                <Input
                  value={f.source_url}
                  onChange={(e) => upd("source_url", e.target.value)}
                  placeholder="https://…"
                  data-testid="mp-source-url"
                />
              </div>
            </div>

            <div>
              <Label>Product description <span className="text-erf-goldHover">*</span></Label>
              <Input
                value={f.product_description}
                onChange={(e) => upd("product_description", e.target.value)}
                placeholder='e.g. "2x4x8 KD Pine Stud"'
                data-testid="mp-product"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Manufacturer</Label>
                <Input value={f.manufacturer} onChange={(e) => upd("manufacturer", e.target.value)} data-testid="mp-manufacturer" />
              </div>
              <div>
                <Label>Model / SKU</Label>
                <Input value={f.model_sku} onChange={(e) => upd("model_sku", e.target.value)} data-testid="mp-sku" />
              </div>
              <div>
                <Label>Unit of measure <span className="text-erf-goldHover">*</span></Label>
                <Input value={f.unit_of_measure} onChange={(e) => upd("unit_of_measure", e.target.value)} data-testid="mp-uom" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Price per unit (USD) <span className="text-erf-goldHover">*</span></Label>
                <Input type="number" step="0.01" value={f.price_per_unit} onChange={(e) => upd("price_per_unit", e.target.value)} data-testid="mp-price" />
              </div>
              <div>
                <Label>Quantity required</Label>
                <Input type="number" step="0.01" value={f.quantity_required} onChange={(e) => upd("quantity_required", e.target.value)} data-testid="mp-qty" />
              </div>
              <div>
                <Label>Observed at <span className="text-erf-goldHover">*</span></Label>
                <Input type="datetime-local" value={f.observed_at} onChange={(e) => upd("observed_at", e.target.value)} data-testid="mp-observed" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Store or supplier <span className="text-erf-goldHover">*</span></Label>
                <Input value={f.store_or_supplier} onChange={(e) => upd("store_or_supplier", e.target.value)} data-testid="mp-supplier" />
              </div>
              <div>
                <Label>Supplier location</Label>
                <Input value={f.supplier_location} onChange={(e) => upd("supplier_location", e.target.value)} data-testid="mp-location" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Freight</Label>
                <Input type="number" step="0.01" value={f.freight} onChange={(e) => upd("freight", e.target.value)} data-testid="mp-freight" />
              </div>
              <div>
                <Label>Taxes</Label>
                <Input type="number" step="0.01" value={f.taxes} onChange={(e) => upd("taxes", e.target.value)} data-testid="mp-taxes" />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input value={f.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="mp-notes" />
            </div>

            {meta?.allows_upload && (
              <div className="border border-dashed border-erf-borderCream rounded-md p-3">
                <Label className="flex items-center gap-1.5">
                  <Upload className="w-4 h-4" /> Attach receipt / screenshot (PDF, PNG, JPG, WEBP, DOCX, XLSX)
                </Label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1"
                  data-testid="mp-file"
                />
                {file && <div className="text-[11px] font-mono text-erf-dark/60 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/procurement/prices")} className="border-erf-borderCream" data-testid="mp-cancel">
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="mp-save">
            <Save className="w-4 h-4 mr-1.5" /> {submitting ? "Saving…" : "Record price"}
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
